import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from 'react-toastify';
import {
  fetchMentors,
  fetchMentorBatches,
  removeBatchFromMentor,
  cleanupPartialBatchesForAllMentors,
} from '../../api/mentorApi';
import DataList from './DataList';
import DataCard from './DataCard';
import Pagination from '../common/Pagination';
import AssignBatchModal from './AssignBatchModal';

const ITEMS_PER_PAGE = 10;

const formatMentorName = (mentor) =>
  mentor.displayName ||
  [mentor.firstName, mentor.lastName].filter(Boolean).join(' ') ||
  mentor.username ||
  `Mentor #${mentor.id}`;

const MentorsSection = () => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  // { [mentorId]: { loading, batches } }
  const [nestedData, setNestedData] = useState({});

  // Assign-batch modal state
  const [assignTarget, setAssignTarget] = useState(null);

  // Remove-batch spinner state: `${mentorId}:${batchId}`
  const [removingKey, setRemovingKey] = useState(null);

  // Per-mentor batch-list filter: 'all' | 'partial' | 'full'
  const [batchFilter, setBatchFilter] = useState({});
  const getFilter = (mentorId) => batchFilter[mentorId] || 'all';
  const setFilter = (mentorId, value) =>
    setBatchFilter((prev) => ({ ...prev, [mentorId]: value }));

  // Global "clean up partial across all mentors" spinner
  const [globalCleanupRunning, setGlobalCleanupRunning] = useState(false);

  // Password-confirm modal for the destructive bulk cleanup
  const [cleanupPasswordOpen, setCleanupPasswordOpen] = useState(false);
  const [cleanupPassword, setCleanupPassword] = useState('');
  const [cleanupPasswordError, setCleanupPasswordError] = useState(null);

  const loadMentors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchMentors(currentPage, ITEMS_PER_PAGE, searchTerm);
      setMentors(data.mentors || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error loading mentors:', err);
      setError('Failed to load mentors. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadMentors();
  }, [loadMentors]);

  const loadMentorBatches = useCallback(async (mentorId) => {
    setNestedData((prev) => ({
      ...prev,
      [mentorId]: { loading: true, batches: prev[mentorId]?.batches || [] },
    }));
    try {
      const data = await fetchMentorBatches(mentorId);
      setNestedData((prev) => ({
        ...prev,
        [mentorId]: { loading: false, batches: data.batches || [] },
      }));
    } catch (err) {
      console.error(`Error loading batches for mentor ${mentorId}:`, err);
      toast.error('Failed to load mentor batches');
      setNestedData((prev) => ({
        ...prev,
        [mentorId]: { loading: false, batches: [] },
      }));
    }
  }, []);

  const handleToggle = useCallback(
    async (mentor) => {
      if (expandedId === mentor.id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(mentor.id);
      if (!nestedData[mentor.id]) {
        await loadMentorBatches(mentor.id);
      }
    },
    [expandedId, nestedData, loadMentorBatches]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedId(null);
  };

  const handleRemoveBatch = async (mentor, batch) => {
    const key = `${mentor.id}:${batch.id}`;
    const confirmMsg = `Remove batch "${batch.name}" from ${formatMentorName(mentor)}?\n\nThis will unlink this mentor from every student in that batch.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) return;

    setRemovingKey(key);
    try {
      const result = await removeBatchFromMentor(mentor.id, batch.id);
      toast.success(
        result.removedStudentCount > 0
          ? `Unlinked ${result.removedStudentCount} student${result.removedStudentCount === 1 ? '' : 's'}.`
          : 'No active links to remove.'
      );
      await loadMentorBatches(mentor.id);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to remove batch';
      toast.error(msg);
    } finally {
      setRemovingKey(null);
    }
  };

  const handleAssigned = async () => {
    if (assignTarget) {
      await loadMentorBatches(assignTarget.id);
    }
    setAssignTarget(null);
  };

  const handleCleanupAllMentorsPartial = () => {
    setCleanupPassword('');
    setCleanupPasswordError(null);
    setCleanupPasswordOpen(true);
  };

  const closeCleanupPasswordModal = () => {
    if (globalCleanupRunning) return;
    setCleanupPasswordOpen(false);
    setCleanupPassword('');
    setCleanupPasswordError(null);
  };

  const confirmCleanupAllMentorsPartial = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!cleanupPassword) {
      setCleanupPasswordError('Enter your admin password to confirm.');
      return;
    }

    setGlobalCleanupRunning(true);
    setCleanupPasswordError(null);
    try {
      const result = await cleanupPartialBatchesForAllMentors({
        search: searchTerm,
        dryRun: false,
        password: cleanupPassword,
      });

      if (result.totalPartialBatches === 0) {
        toast.info(
          searchTerm
            ? 'No partial batches found for mentors matching this search.'
            : 'No partial batches found across any mentors.'
        );
      } else {
        toast.success(
          `Cleaned up ${result.totalPartialBatches} partial batch` +
            `${result.totalPartialBatches === 1 ? '' : 'es'} across ` +
            `${result.mentorsWithPartial} mentor${result.mentorsWithPartial === 1 ? '' : 's'} — ` +
            `${result.totalStudentsUnlinked} student link${result.totalStudentsUnlinked === 1 ? '' : 's'} removed.`
        );
      }

      setCleanupPasswordOpen(false);
      setCleanupPassword('');
      setNestedData({});
      await loadMentors();
      if (expandedId) {
        await loadMentorBatches(expandedId);
      }
    } catch (err) {
      const status = err.response?.status;
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to clean up partial batches';
      // Keep the modal open on auth/password errors so the admin can retry.
      if (status === 401 || status === 400) {
        setCleanupPasswordError(msg);
      } else {
        toast.error(msg);
        setCleanupPasswordOpen(false);
        setCleanupPassword('');
      }
    } finally {
      setGlobalCleanupRunning(false);
    }
  };

  const handleRemoveAllPartial = async (mentor, partialBatches) => {
    if (partialBatches.length === 0) return;
    const confirmMsg =
      `Remove ${partialBatches.length} partially-linked batch` +
      `${partialBatches.length === 1 ? '' : 'es'} from ${formatMentorName(mentor)}?\n\n` +
      `This will unlink this mentor from every student in those batches that is currently linked.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) return;

    setRemovingKey(`${mentor.id}:*partial`);
    try {
      const results = await Promise.allSettled(
        partialBatches.map((b) => removeBatchFromMentor(mentor.id, b.id))
      );
      const successes = results.filter((r) => r.status === 'fulfilled');
      const failures = results.filter((r) => r.status === 'rejected');
      const removedStudentCount = successes.reduce(
        (sum, r) => sum + (r.value?.removedStudentCount || 0),
        0
      );
      if (successes.length > 0) {
        toast.success(
          `Removed ${successes.length} batch${successes.length === 1 ? '' : 'es'}` +
            (removedStudentCount > 0
              ? ` — unlinked ${removedStudentCount} student${removedStudentCount === 1 ? '' : 's'}.`
              : '.')
        );
      }
      if (failures.length > 0) {
        toast.error(
          `${failures.length} batch${failures.length === 1 ? '' : 'es'} failed to remove`
        );
      }
      await loadMentorBatches(mentor.id);
    } finally {
      setRemovingKey(null);
    }
  };

  return (
    <div>
      {error && (
        <div className="data-error-banner">
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <div className="mentors-section-toolbar">
        <span className="mentors-section-toolbar-hint">
          <i className="bi bi-info-circle" />{' '}
          {searchTerm
            ? <>Bulk actions apply to mentors matching <strong>“{searchTerm}”</strong>.</>
            : 'Bulk actions apply to every mentor in the system.'}
        </span>
        <button
          type="button"
          className="mentors-btn mentors-btn--danger"
          onClick={handleCleanupAllMentorsPartial}
          disabled={globalCleanupRunning || loading}
          title="Find every partially-linked batch across mentors and remove those partial links"
        >
          {globalCleanupRunning ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              Cleaning up…
            </>
          ) : (
            <>
              <i className="bi bi-trash3" /> Remove all partial (all mentors)
            </>
          )}
        </button>
      </div>

      <DataList
        searchPlaceholder="Search mentors by name, email or username…"
        searchValue={searchTerm}
        onSearch={handleSearch}
        loading={loading}
        empty={!loading && mentors.length === 0}
        emptyMessage={searchTerm ? 'No mentors match your search.' : 'No mentors found.'}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      >
        {mentors.map((mentor) => {
          const nested = nestedData[mentor.id];
          const isExpanded = expandedId === mentor.id;
          const batchCount = nested?.batches?.length;
          const badgeText =
            batchCount !== undefined
              ? `${batchCount} Batch${batchCount === 1 ? '' : 'es'}`
              : `${mentor._count?.students ?? 0} Students`;

          return (
            <DataCard
              key={mentor.id}
              title={formatMentorName(mentor)}
              subtitle={mentor.email || mentor.username}
              badge={badgeText}
              accentColor="var(--accent-orange)"
              expanded={isExpanded}
              onToggle={() => handleToggle(mentor)}
            >
              {!nested || nested.loading ? (
                <div className="data-nested-skeleton">
                  <Skeleton count={3} height={36} borderRadius={6} />
                </div>
              ) : (
                (() => {
                  const allBatches = nested?.batches || [];
                  const partialBatches = allBatches.filter(
                    (b) => b.assignedStudentsCount < b.totalStudents
                  );
                  const fullBatches = allBatches.filter(
                    (b) => b.assignedStudentsCount >= b.totalStudents
                  );
                  const filter = getFilter(mentor.id);
                  const filtered =
                    filter === 'partial'
                      ? partialBatches
                      : filter === 'full'
                      ? fullBatches
                      : allBatches;
                  const bulkKey = `${mentor.id}:*partial`;
                  const isBulkRemoving = removingKey === bulkKey;
                  const totalLinked = allBatches.reduce(
                    (sum, b) => sum + (b.assignedStudentsCount || 0),
                    0
                  );
                  const totalStudents = allBatches.reduce(
                    (sum, b) => sum + (b.totalStudents || 0),
                    0
                  );
                  const linkedPct = totalStudents
                    ? Math.round((totalLinked / totalStudents) * 100)
                    : 0;

                  return (
                    <>
                      <div className="mentors-batches-header">
                        <span className="mentors-batches-label">
                          <i className="bi bi-people-fill" /> Assigned batches
                          {allBatches.length > 0 && (
                            <span
                              className="mentors-link-summary"
                              title="Students in these batches who are linked to this mentor, out of every student in these batches"
                            >
                              {' '}
                              — <strong>{totalLinked}</strong>/{totalStudents} students linked
                              {' '}({linkedPct}%)
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          className="mentors-btn mentors-btn--primary mentors-btn--sm"
                          onClick={() => setAssignTarget(mentor)}
                        >
                          <i className="bi bi-plus-lg" /> Assign batch
                        </button>
                      </div>

                      {allBatches.length > 0 && (
                        <div className="mentors-filter-bar">
                          <div
                            className="mentors-filter-pills"
                            role="tablist"
                            aria-label="Filter batches"
                          >
                            <button
                              type="button"
                              role="tab"
                              aria-selected={filter === 'all'}
                              className={`mentors-filter-pill${filter === 'all' ? ' active' : ''}`}
                              onClick={() => setFilter(mentor.id, 'all')}
                            >
                              All <span className="mentors-filter-count">{allBatches.length}</span>
                            </button>
                            <button
                              type="button"
                              role="tab"
                              aria-selected={filter === 'partial'}
                              className={`mentors-filter-pill mentors-filter-pill--partial${filter === 'partial' ? ' active' : ''}`}
                              onClick={() => setFilter(mentor.id, 'partial')}
                              disabled={partialBatches.length === 0 && filter !== 'partial'}
                              title="Batches where not every student is linked to this mentor"
                            >
                              <i className="bi bi-exclamation-circle-fill" /> Partial{' '}
                              <span className="mentors-filter-count">{partialBatches.length}</span>
                            </button>
                            <button
                              type="button"
                              role="tab"
                              aria-selected={filter === 'full'}
                              className={`mentors-filter-pill${filter === 'full' ? ' active' : ''}`}
                              onClick={() => setFilter(mentor.id, 'full')}
                              disabled={fullBatches.length === 0 && filter !== 'full'}
                            >
                              <i className="bi bi-check-circle-fill" /> Fully linked{' '}
                              <span className="mentors-filter-count">{fullBatches.length}</span>
                            </button>
                          </div>
                          {partialBatches.length > 0 && (
                            <button
                              type="button"
                              className="mentors-btn mentors-btn--danger mentors-btn--sm"
                              onClick={() => handleRemoveAllPartial(mentor, partialBatches)}
                              disabled={isBulkRemoving}
                              title="Unlink this mentor from every student in each partially-linked batch"
                            >
                              {isBulkRemoving ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Removing…
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-trash" /> Remove all partial
                                  {' '}({partialBatches.length})
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}

                      {allBatches.length === 0 ? (
                        <div className="data-nested-empty">
                          No batches linked to this mentor yet.
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="data-nested-empty">
                          {filter === 'partial'
                            ? 'No partially-linked batches. Everything is fully linked.'
                            : 'No fully-linked batches in this view.'}
                        </div>
                      ) : (
                        <div className="mentors-chip-list">
                          {filtered.map((batch) => {
                            const key = `${mentor.id}:${batch.id}`;
                            const isRemoving = removingKey === key;
                            const partial =
                              batch.assignedStudentsCount < batch.totalStudents;
                        return (
                          <div key={batch.id} className="mentors-chip">
                            <div className="mentors-chip-main">
                              <span className="mentors-chip-name">{batch.name}</span>
                              <span className="mentors-chip-meta">
                                {batch.assignedStudentsCount}/{batch.totalStudents}{' '}
                                students linked
                                {partial && (
                                  <span
                                    className="mentors-chip-partial"
                                    title="Not every student in this batch is linked to this mentor"
                                  >
                                    <i className="bi bi-exclamation-circle-fill" /> partial
                                  </span>
                                )}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="mentors-chip-remove"
                              onClick={() => handleRemoveBatch(mentor, batch)}
                              disabled={isRemoving}
                              aria-label={`Remove batch ${batch.name}`}
                              title="Remove batch"
                            >
                              {isRemoving ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                              ) : (
                                <i className="bi bi-trash" />
                              )}
                            </button>
                          </div>
                        );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </DataCard>
          );
        })}
      </DataList>

      {assignTarget && (
        <AssignBatchModal
          mentor={assignTarget}
          excludeBatchIds={
            new Set(
              (nestedData[assignTarget.id]?.batches || []).map((b) => b.id)
            )
          }
          onClose={() => setAssignTarget(null)}
          onAssigned={handleAssigned}
        />
      )}

      {cleanupPasswordOpen && (
        <div className="mentors-modal-overlay" onClick={closeCleanupPasswordModal}>
          <div className="mentors-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mentors-modal-header">
              <h2 className="mentors-modal-title">
                <i
                  className="bi bi-shield-lock-fill"
                  style={{ color: 'var(--accent-orange)' }}
                  aria-hidden="true"
                />
                Confirm bulk cleanup
              </h2>
              <button
                className="mentors-modal-close"
                onClick={closeCleanupPasswordModal}
                disabled={globalCleanupRunning}
                aria-label="Close"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>

            <form onSubmit={confirmCleanupAllMentorsPartial}>
              <div className="mentors-modal-body">
                <p style={{ marginTop: 0 }}>
                  You are about to remove every partially-linked batch from{' '}
                  <strong>
                    {searchTerm
                      ? `mentors matching "${searchTerm}"`
                      : 'ALL mentors'}
                  </strong>
                  . This unlinks those mentors from any student who isn't fully
                  covered by their batch link.{' '}
                  <strong>This action cannot be undone.</strong>
                </p>
                <p>Re-enter your admin password to continue.</p>

                <label
                  htmlFor="cleanup-confirm-password"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}
                >
                  Admin password
                </label>
                <input
                  id="cleanup-confirm-password"
                  type="password"
                  className="data-list-search"
                  style={{ width: '100%' }}
                  value={cleanupPassword}
                  onChange={(e) => {
                    setCleanupPassword(e.target.value);
                    if (cleanupPasswordError) setCleanupPasswordError(null);
                  }}
                  autoFocus
                  autoComplete="current-password"
                  disabled={globalCleanupRunning}
                  placeholder="Enter your password"
                />
                {cleanupPasswordError && (
                  <div
                    className="data-error-banner"
                    style={{ marginTop: 12 }}
                    role="alert"
                  >
                    <i className="bi bi-exclamation-triangle-fill" />
                    {cleanupPasswordError}
                  </div>
                )}
              </div>

              <div className="mentors-modal-footer">
                <button
                  type="button"
                  className="mentors-btn"
                  onClick={closeCleanupPasswordModal}
                  disabled={globalCleanupRunning}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="mentors-btn mentors-btn--danger"
                  disabled={globalCleanupRunning || !cleanupPassword}
                >
                  {globalCleanupRunning ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Cleaning up…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash3" /> Confirm &amp; remove
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorsSection;
